/**
 * atom-custom-length.js
 * Mutual-exclusion engine for the custom cut length input vs. variant pills.
 * Also recalculates and displays the price when a cut length is entered.
 *
 * Dependencies: none (vanilla ES6, no jQuery).
 * Works alongside the existing apdpSelectVariantPill / apdpUpdateVariant
 * functions defined in atom-product-main.liquid.
 *
 * Idempotent: the IIFE guard prevents double-binding if the script loads twice.
 */

(function () {
  'use strict';

  if (window.__atomCutLengthInit__) return;
  window.__atomCutLengthInit__ = true;

  // ── Constants ─────────────────────────────────────────────────────────────
  var FADE_MS     = 4000;  // ms before the hint auto-fades
  var MSG_ID      = 'apdp-cut-msg';
  var LIVE_ID     = 'apdp-cut-live';
  var SUBTITLE_ID = 'apdp-cut-price-subtitle';
  var MTM_CONTACT_FRAME_NAME = 'apdp-mtm-contact-submit-frame';
  var DISABLED_CLS = 'buybox-pill--disabled';
  var INVALID_LENGTH_MESSAGE = 'Enter a valid length in metres using numbers only, for example 2.2.';
  var UNAVAILABLE_LENGTH_MESSAGE = 'This length is not available with your selected product options. Clear the Made to Measure value and choose another option.';

  // ── State ─────────────────────────────────────────────────────────────────
  var _priorVariantId   = null;  // variant id the customer had before typing
  var _priorPillValues  = {};    // { optionPosition: selectedValue }
  var _cutLengthActive  = false;
  var _fadeTimer        = null;
  var _contactModalTrigger = null;
  var _contactInertElements = [];

  // ── DOM helpers ───────────────────────────────────────────────────────────
  function getInput()   { return document.getElementById('apdp-cut-length'); }
  function getCableSelect() {
    return document.getElementById('apdp-cable-length') ||
      document.querySelector('#apdp-product-form [data-switchboard-addon-select][data-switchboard-property="cable_length"]');
  }
  function getAddonSelects() {
    return Array.prototype.slice.call(
      document.querySelectorAll('#apdp-product-form [data-switchboard-addon-select]')
    );
  }
  function getVariantId() { return document.getElementById('apdpVariantId'); }
  function getPills()   { return document.querySelectorAll('#apdp-root .buybox-pill'); }
  function getSelects() { return document.querySelectorAll('#apdp-root .buybox-select'); }
  function getPriceEl() { return document.getElementById('apdpPrice'); }
  function getForm() { return document.getElementById('apdp-product-form'); }

  // ── Parse the input value; returns a positive number or null ──────────────
  function parseLengthValue(value) {
    var raw = String(value || '').trim().replace(',', '.');
    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw)) return null;
    var n = Number(raw);
    return (isFinite(n) && n > 0) ? n : null;
  }

  function parseLength(input) {
    return parseLengthValue(input.value || '');
  }

  // Extract numeric metres from a variant option string, e.g. "5m" -> 5.
  function toMetres(str) {
    var m = String(str || '').match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  // Find which option index is the "length/size" axis (0-based).
  function getLengthOptionIndex() {
    var labelledPositions = [];
    var selects = document.querySelectorAll('#apdp-root .buybox-select');
    selects.forEach(function (sel, idx) {
      var field = sel.closest ? sel.closest('.buybox-field--pills') : null;
      var label = field ? field.querySelector('.buybox-field-label') : null;
      if (!label) return;
      var name = label.textContent.trim().toLowerCase();
      if (name === 'length' || name === 'size') labelledPositions.push(idx);
    });

    if (labelledPositions.length === 1) return labelledPositions[0];
    if (labelledPositions.length > 1) return -1;

    var inferredPositions = [];
    if (window.__apdpVariants__ && window.__apdpVariants__.length) {
      var sample = window.__apdpVariants__[0].options || [];
      sample.forEach(function (_value, idx) {
        var hasMetreOption = window.__apdpVariants__.some(function (variant) {
          var option = String((variant.options || [])[idx] || '').trim();
          return /(?:\d+(?:\.\d+)?\s*(?:metre|meter)s?\b)|(?:^\d+(?:\.\d+)?\s*m$)/i.test(option);
        });
        if (hasMetreOption) inferredPositions.push(idx);
      });
    }
    return inferredPositions.length === 1 ? inferredPositions[0] : -1;
  }

  function getSelectedOptionValues() {
    return Array.prototype.map.call(getSelects(), function (select) {
      return select.value;
    });
  }

  function variantMatchesSelectedNonLengthOptions(variant, selectedOptions, lengthPos) {
    var options = variant.options || [];
    if (options.length !== selectedOptions.length) return false;
    return selectedOptions.every(function (selectedValue, index) {
      if (index === lengthPos) return true;
      return String(options[index] || '') === String(selectedValue || '');
    });
  }

  function getCompatibleVariants() {
    var variants = window.__apdpVariants__ || [];
    var lengthPos = getLengthOptionIndex();
    if (!variants.length || lengthPos < 0) return [];

    var selectedOptions = getSelectedOptionValues();
    return variants.filter(function (variant) {
      return variantMatchesSelectedNonLengthOptions(variant, selectedOptions, lengthPos);
    });
  }

  function getLengthVariantRows() {
    var variants = getCompatibleVariants();
    var lengthPos = getLengthOptionIndex();
    if (!variants.length || lengthPos < 0) return [];

    var available = variants.filter(function (v) { return v.available; });

    return available
      .map(function (v) {
        return { variant: v, metres: toMetres(v.options[lengthPos]) };
      })
      .filter(function (row) { return row.metres !== null; });
  }

  function getMaxVariantMetres() {
    var rows = getLengthVariantRows();
    if (!rows.length) return null;
    return rows.reduce(function (max, row) {
      return row.metres > max ? row.metres : max;
    }, 0);
  }

  function isOverMaxVariantLength(metres) {
    var maxMetres = getMaxVariantMetres();
    if (!maxMetres) return false;
    return Math.ceil(metres) > maxMetres;
  }

  function formatMetres(value) {
    return Number(value) % 1 === 0 ? String(parseInt(value, 10)) : String(value);
  }

  function formatMetresWithUnit(value) {
    var metres = formatMetres(value);
    return metres + 'm';
  }

  function buildMtmLimitMessage(maxMetres) {
    var limit = maxMetres ? ' ' + formatMetresWithUnit(maxMetres) : '';
    return 'Only max length allowed' + limit + '. For longer lengths contact us.';
  }

  function getSelectedVariant() {
    var idEl = getVariantId();
    var variants = window.__apdpVariants__ || [];
    if (!idEl || !variants.length) return null;
    var id = parseInt(idEl.value, 10);
    if (!id) return null;
    return variants.find(function (variant) { return variant.id === id; }) || null;
  }

  function getSelectedSku() {
    var variant = getSelectedVariant();
    if (variant && variant.sku) return variant.sku;

    var skuEl = document.getElementById('apdpSku');
    if (!skuEl) return '';
    return skuEl.textContent.replace(/^SKU:\s*/i, '').trim();
  }

  function getContactModal() {
    return document.getElementById('apdp-mtm-contact-modal');
  }

  function getMtmContactForm() {
    var modal = getContactModal();
    return (modal && modal.querySelector('#apdp-mtm-contact-form')) ||
      document.getElementById('apdp-mtm-contact-form');
  }

  function getMtmContactFocusable(modal) {
    if (!modal) return [];
    return Array.prototype.filter.call(
      modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (element) {
        return element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
      }
    );
  }

  function setMtmContactBackgroundInert(shouldInert) {
    if (!shouldInert) {
      _contactInertElements.forEach(function (entry) {
        if (!entry.element || !entry.element.isConnected) return;
        entry.element.inert = entry.wasInert;
        if (!entry.hadAttribute && !entry.wasInert) entry.element.removeAttribute('inert');
      });
      _contactInertElements = [];
      return;
    }

    var modal = getContactModal();
    if (!modal || _contactInertElements.length) return;

    var branch = modal;
    while (branch && branch !== document.body) {
      var parent = branch.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (sibling) {
        if (sibling === branch) return;
        _contactInertElements.push({
          element: sibling,
          wasInert: Boolean(sibling.inert),
          hadAttribute: sibling.hasAttribute('inert')
        });
        sibling.inert = true;
      });
      branch = parent;
    }
  }

  function ensureMtmContactSubmitFrame() {
    var frame = document.getElementById(MTM_CONTACT_FRAME_NAME);
    if (frame) return frame;

    frame = document.createElement('iframe');
    frame.id = MTM_CONTACT_FRAME_NAME;
    frame.name = MTM_CONTACT_FRAME_NAME;
    frame.title = 'Made To Measure request submission';
    frame.hidden = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.display = 'none';
    document.body.appendChild(frame);
    return frame;
  }

  function prepareMtmContactBackgroundSubmit(form) {
    if (!form) return;
    var frame = ensureMtmContactSubmitFrame();
    if (frame && frame.name) form.setAttribute('target', frame.name);
  }

  function warmMtmContactProtection() {
    var form = getMtmContactForm();
    if (!form || form.dataset.apdpProtectionWarm || form.dataset.cptcha || form.dataset.hcaptchaBound || form.dataset.recaptchaBound) return;

    var captcha = window.Shopify && window.Shopify.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return;

    form.dataset.apdpProtectionWarm = 'true';
    try {
      captcha.protect(form, function () {
        form.dataset.apdpProtectionReady = 'true';
      });
    } catch (_err) {
      delete form.dataset.apdpProtectionWarm;
    }
  }

  function syncContactProductFields() {
    var modal = getContactModal();
    if (!modal) return;

    var sku = getSelectedSku();
    var maxMetres = getMaxVariantMetres();
    var productTitle = modal.getAttribute('data-product-title') || '';
    var productInput = modal.querySelector('[data-apdp-contact-product]');
    var skuInput = modal.querySelector('[data-apdp-contact-sku]');
    var skuText = modal.querySelector('[data-apdp-contact-sku-text]');
    var maxInput = modal.querySelector('[data-apdp-contact-max]');

    if (productInput) productInput.value = productTitle;
    if (skuInput) skuInput.value = sku;
    if (skuText) skuText.textContent = sku || 'Selected variant';
    if (maxInput) maxInput.value = maxMetres ? formatMetresWithUnit(maxMetres) : '';
  }

  function clearMtmContactPostedUrl() {
    if (!window.history || !window.URL) return;

    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted') && url.searchParams.get('mtm_contact') !== 'sent') return;

      url.searchParams.delete('contact_posted');
      url.searchParams.delete('mtm_contact');
      if (url.hash === '#contact_form' || url.hash === '#apdp-mtm-contact-form') {
        url.hash = '';
      }
      window.history.replaceState(window.history.state, document.title, url.toString());
    } catch (_err) {
      /* Keep the success flow working if URL parsing is unavailable. */
    }
  }

  function hasMtmContactSuccessSignal() {
    var modal = getContactModal();
    var success = modal ? modal.querySelector('[data-apdp-contact-state="success"]') : null;
    if (success && !success.hidden) return true;

    if (!window.URL) return false;

    try {
      var url = new URL(window.location.href);
      return url.searchParams.get('mtm_contact') === 'sent';
    } catch (_err) {
      return false;
    }
  }

  function resetMtmContactStatus() {
    var modal = getContactModal();
    if (!modal) return;

    modal.querySelectorAll('[data-apdp-contact-state]').forEach(function (status) {
      status.hidden = true;
      status.setAttribute('aria-hidden', 'true');
    });
  }

  function setMtmContactStatus(kind, message) {
    var modal = getContactModal();
    if (!modal) return;

    resetMtmContactStatus();

    var status = modal.querySelector('[data-apdp-contact-state="' + kind + '"]');
    if (!status) return;

    if (message && kind === 'error') {
      var text = status.querySelector('[data-apdp-contact-error-text]');
      if (text) text.textContent = message;
      else status.textContent = message;
    }

    status.hidden = false;
    status.removeAttribute('hidden');
    status.setAttribute('aria-hidden', 'false');
  }

  function resetMtmContactFormForNewRequest() {
    var modal = getContactModal();
    if (!modal) return;

    var form = getMtmContactForm();
    if (form) {
      delete form.dataset.apdpMtmSubmitted;
      form.removeAttribute('target');
    }
    setMtmContactSubmitting(form, false);
    if (form && typeof form.reset === 'function') form.reset();
    resetMtmContactStatus();
    syncContactProductFields();
  }

  function setMtmContactSubmitting(form, isSubmitting) {
    var button = form ? form.querySelector('.apdp-mtm-contact__submit') : null;
    if (!button) return;

    if (!button.dataset.apdpDefaultLabel) {
      button.dataset.apdpDefaultLabel = button.textContent;
    }

    button.disabled = isSubmitting;
    button.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
    button.textContent = isSubmitting ? 'Sending request...' : button.dataset.apdpDefaultLabel;

    if (isSubmitting) {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }
  }

  function setMtmContactSubmitted(form) {
    var button = form ? form.querySelector('.apdp-mtm-contact__submit') : null;
    if (!form || !button) return;

    if (!button.dataset.apdpDefaultLabel) {
      button.dataset.apdpDefaultLabel = button.textContent;
    }

    form.dataset.apdpMtmSubmitted = 'true';
    form.removeAttribute('aria-busy');
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Request sent';
  }

  function handleMtmContactSubmit(event) {
    var form = event.currentTarget;
    if (!form) return;

    if (form.dataset.apdpMtmSubmitted === 'true' || form.dataset.apdpMtmSubmitted === 'pending') {
      event.preventDefault();
      return;
    }

    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    prepareMtmContactBackgroundSubmit(form);
    resetMtmContactStatus();
    syncContactProductFields();
    setMtmContactStatus('sending');
    setMtmContactSubmitting(form, true);
    form.dataset.apdpMtmSubmitted = 'pending';

    window.setTimeout(function () {
      if (form.dataset.apdpMtmSubmitted !== 'pending') return;
      setMtmContactStatus('success');
      setMtmContactSubmitted(form);
    }, 350);
  }

  function openMtmContactModal(trigger, options) {
    var modal = getContactModal();
    if (!modal) return false;

    options = options || {};
    _contactModalTrigger = trigger || document.activeElement;
    if (options.preserveStatus) {
      syncContactProductFields();
    } else {
      resetMtmContactFormForNewRequest();
    }
    modal.hidden = false;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.classList.add('apdp-mtm-contact-open');
    setMtmContactBackgroundInert(true);
    warmMtmContactProtection();

    var target = options.preserveStatus
      ? modal.querySelector('[data-apdp-contact-state="success"]:not([hidden])') ||
        modal.querySelector('[data-apdp-mtm-contact-close]')
      : modal.querySelector('input[name="contact[name]"]') ||
      modal.querySelector('input, textarea, button');
    if (target) {
      setTimeout(function () { target.focus(); }, 40);
    }
    return true;
  }

  function closeMtmContactModal() {
    var modal = getContactModal();
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('apdp-mtm-contact-open');
    setMtmContactBackgroundInert(false);
    if (_contactModalTrigger && typeof _contactModalTrigger.focus === 'function') {
      _contactModalTrigger.focus();
    }
  }

  function stopMtmContactEvent(event) {
    if (!event) return;
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  }

  function requestMtmContactModal(trigger, event) {
    stopMtmContactEvent(event);
    return openMtmContactModal(trigger);
  }

  function handleMtmContactActivation(event) {
    if (!event.target || !event.target.closest) return;

    var opener = event.target.closest('[data-apdp-mtm-contact-open]');
    if (opener) {
      requestMtmContactModal(opener, event);
      return;
    }

    if (event.target.closest('[data-apdp-mtm-contact-close]')) {
      stopMtmContactEvent(event);
      closeMtmContactModal();
      return;
    }

    var modal = getContactModal();
    if (
      modal &&
      modal.classList.contains('is-open') &&
      modal.contains(event.target) &&
      !event.target.closest('.apdp-mtm-contact__panel')
    ) {
      stopMtmContactEvent(event);
      closeMtmContactModal();
    }
  }

  function handleMtmContactKeydown(event) {
    if (event.target && event.target.closest) {
      var opener = event.target.closest('[data-apdp-mtm-contact-open]');
      if (opener && (event.key === 'Enter' || event.key === ' ')) {
        requestMtmContactModal(opener, event);
        return;
      }
    }
    var modal = getContactModal();
    if (!modal || !modal.classList.contains('is-open')) return;

    if (event.key === 'Tab') {
      var focusable = getMtmContactFocusable(modal);
      if (!focusable.length) {
        event.preventDefault();
        var panel = modal.querySelector('.apdp-mtm-contact__panel');
        if (panel) {
          panel.setAttribute('tabindex', '-1');
          panel.focus();
        }
        return;
      }

      // Drive the whole sequence explicitly. Safari/WebKit can omit buttons from
      // normal Tab order depending on the user's keyboard-navigation preference;
      // boundary-only trapping would then allow focus to escape the dialog.
      event.preventDefault();
      var activeIndex = focusable.indexOf(document.activeElement);
      var nextIndex;
      if (activeIndex < 0) nextIndex = event.shiftKey ? focusable.length - 1 : 0;
      else if (event.shiftKey) nextIndex = (activeIndex - 1 + focusable.length) % focusable.length;
      else nextIndex = (activeIndex + 1) % focusable.length;
      focusable[nextIndex].focus();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMtmContactModal();
    }
  }

  function visiblePillOptionValues() {
    var pills = getPills();
    var values = [];
    var coherent = true;

    getSelects().forEach(function (select, index) {
      var position = String(index + 1);
      var group = Array.prototype.filter.call(pills, function (pill) {
        return pill.getAttribute('data-option-position') === position;
      });
      var hasSplitState = group.some(function (pill) {
        return pill.classList.contains('is-selected') !==
          (pill.getAttribute('aria-checked') === 'true');
      });
      var selected = group.filter(function (pill) {
        return pill.classList.contains('is-selected') &&
          pill.getAttribute('aria-checked') === 'true';
      });

      if (hasSplitState || selected.length !== 1) {
        coherent = false;
        return;
      }

      var value = selected[0].getAttribute('data-option-value');
      var hasOption = Array.prototype.some.call(select.options || [], function (option) {
        return String(option.value) === String(value);
      });
      if (!hasOption) {
        coherent = false;
        return;
      }
      values[index] = value;
    });

    return coherent && values.length === getSelects().length ? values : null;
  }

  function exactVariantForOptions(values) {
    if (!values || !window.__apdpVariants__) return null;
    var matches = window.__apdpVariants__.filter(function (variant) {
      var options = variant.options || [];
      return options.length === values.length && options.every(function (value, index) {
        return String(value) === String(values[index]);
      });
    });
    return matches.length === 1 ? matches[0] : null;
  }

  function isBackForwardNavigation() {
    try {
      var entries = window.performance && window.performance.getEntriesByType
        ? window.performance.getEntriesByType('navigation')
        : [];
      if (entries && entries.length) return entries[0].type === 'back_forward';
      return Boolean(window.performance && window.performance.navigation &&
        window.performance.navigation.type === 2);
    } catch (_error) {
      return false;
    }
  }

  function variantPresentationIsCoherent(variant, values) {
    var selectedValues = getSelectedOptionValues();
    var visibleValues = visiblePillOptionValues();
    var idEl = getVariantId();
    var skuEl = document.getElementById('apdpSku');
    var displayedSku = skuEl
      ? String(skuEl.textContent || '').replace(/^\s*SKU:\s*/i, '').trim()
      : '';
    var priceEl = getPriceEl();
    var button = document.querySelector('#apdp-product-form .btn-cart');
    var optionsCoherent = selectedValues.length === values.length &&
      selectedValues.every(function (value, index) {
        return String(value) === String(values[index]);
      });
    var visibleCoherent = visibleValues && visibleValues.length === values.length &&
      visibleValues.every(function (value, index) {
        return String(value) === String(values[index]);
      });
    var availabilityCoherent = !button || button.hasAttribute('disabled') !== Boolean(variant.available);

    return Boolean(
      optionsCoherent &&
      visibleCoherent &&
      idEl &&
      String(idEl.value) === String(variant.id) &&
      (!skuEl || !variant.sku || displayedSku === String(variant.sku)) &&
      (!priceEl || !variant.price ||
        priceEl.getAttribute('data-original-price-pennies') === String(variant.price)) &&
      availabilityCoherent
    );
  }

  function failClosedVisibleVariantSelection() {
    enablePills();
    var idEl = getVariantId();
    if (idEl) idEl.value = '';

    var button = document.querySelector('#apdp-product-form .btn-cart');
    if (button) {
      button.setAttribute('disabled', 'disabled');
      if (!button.dataset.busy) button.textContent = 'Please reselect options';
    }
    announce('Please reselect the product options before adding to basket.');
    return false;
  }

  // When no MTM value is active, the visible pills are the customer's source
  // of truth. Browsers can restore hidden selects and the hidden variant id
  // after a new-document Back navigation, leaving them out of sync with what
  // the customer sees. Repair only actual drift through the canonical updater.
  function reconcileVisibleVariantSelection() {
    // This asset is loaded on product templates that do not necessarily expose
    // Made to Measure. Leave those products on their existing variant path.
    if (!getForm() || !getInput()) return true;

    var values = visiblePillOptionValues();
    var variant = exactVariantForOptions(values);
    var selects = getSelects();
    var idEl = getVariantId();
    if (
      !values ||
      !variant ||
      !selects.length ||
      values.length !== selects.length ||
      !idEl ||
      typeof window.apdpUpdateVariant !== 'function'
    ) return failClosedVisibleVariantSelection();

    if (!variantPresentationIsCoherent(variant, values)) {
      selects.forEach(function (select, index) {
        select.value = values[index];
      });
      idEl.value = variant.id;
      try {
        window.apdpUpdateVariant(selects[0]);
      } catch (_error) {
        return failClosedVisibleVariantSelection();
      }
    }

    return variantPresentationIsCoherent(variant, values)
      ? true
      : failClosedVisibleVariantSelection();
  }

  // ── Snapshot the customer-visible selection before MTM locks the pills ───
  // The visible pills are authoritative. Globo/Clearpay can transiently reset
  // hidden native selects in WebKit while the customer's Natural White pill is
  // still selected; reading that stale hidden state caused the historic colour
  // substitution. A split or ambiguous visual state fails closed.
  function snapshotPills() {
    _priorPillValues = {};
    var values = visiblePillOptionValues();
    if (!values) {
      _priorVariantId = null;
      return false;
    }

    values.forEach(function (value, index) {
      _priorPillValues[String(index + 1)] = value;
    });
    var exactVariant = exactVariantForOptions(values);
    _priorVariantId = exactVariant ? String(exactVariant.id) : null;
    return Boolean(exactVariant);
  }

  // Reconcile every hidden select and pill from the snapshot in one synchronous
  // turn before resolving the billed whole-metre variant. This prevents a
  // third-party hidden-select reset from becoming the source of truth.
  function restoreSnapshotSelectionForMtm() {
    var selects = getSelects();
    if (!selects.length || Object.keys(_priorPillValues).length !== selects.length) return false;

    var valid = true;
    selects.forEach(function (select, index) {
      var position = String(index + 1);
      var value = _priorPillValues[position];
      var hasOption = Array.prototype.some.call(select.options || [], function (option) {
        return String(option.value) === String(value);
      });
      if (!hasOption) {
        valid = false;
        return;
      }
      select.disabled = false;
      select.value = value;
      document.querySelectorAll(
        '#apdp-root .buybox-pill[data-option-position="' + position + '"]'
      ).forEach(function (pill) {
        var selected = pill.getAttribute('data-option-value') === value;
        pill.classList.toggle('is-selected', selected);
        pill.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    });
    return valid;
  }

  // ── Restore the snapshot ──────────────────────────────────────────────────
  function restorePills(syncVariant) {
    Object.keys(_priorPillValues).forEach(function (pos) {
      var val = _priorPillValues[pos];
      var select = document.getElementById('apdp-option-' + pos);
      if (select) {
        select.disabled = false;
        select.value = val;
      }
      document.querySelectorAll(
        '#apdp-root .buybox-pill[data-option-position="' + pos + '"]'
      ).forEach(function (p) {
        var on = (p.getAttribute('data-option-value') === val);
        p.classList.toggle('is-selected', on);
        p.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    });
    var idEl = getVariantId();
    if (idEl && _priorVariantId) idEl.value = _priorVariantId;

    if (syncVariant && _priorVariantId && typeof window.apdpUpdateVariant === 'function') {
      var selects = getSelects();
      if (selects.length) window.apdpUpdateVariant(selects[0]);
    }
  }

  // ── Find the best variant for a given cut length ──────────────────────────
  // Preserves every non-length option and selects the exact whole-metre variant
  // used for billing. If that compatible variant is missing or unavailable, the
  // caller fails closed rather than silently changing colour or another option.
  function findBestVariant(metres) {
    var lengthPos = getLengthOptionIndex();
    if (lengthPos < 0) return null;

    var targetMetres = Math.ceil(metres);
    var matches = getCompatibleVariants().filter(function (variant) {
      return variant.available && toMetres((variant.options || [])[lengthPos]) === targetMetres;
    });

    if (matches.length !== 1) return null;
    return { variant: matches[0], billedMetres: targetMetres };
  }

  // ── Apply only the chosen length + #apdpVariantId ─────────────────────────
  function applyVariant(variant) {
    var selects = getSelects();
    var lengthPos = getLengthOptionIndex();
    var selectedOptions = getSelectedOptionValues();
    if (lengthPos < 0 || !selects[lengthPos] || typeof window.apdpUpdateVariant !== 'function') {
      return false;
    }

    var preservesOptions = variantMatchesSelectedNonLengthOptions(
      variant,
      selectedOptions,
      lengthPos
    );
    if (!preservesOptions) return false;

    var lengthValue = (variant.options || [])[lengthPos];
    if (lengthValue === undefined) return false;

    var lengthSelect = selects[lengthPos];
    var hasLengthOption = Array.prototype.some.call(lengthSelect.options || [], function (option) {
      return String(option.value) === String(lengthValue);
    });
    if (!hasLengthOption) return false;

    var idEl = getVariantId();
    var previousVariantId = idEl ? idEl.value : '';
    lengthSelect.value = lengthValue;
    var position = String(lengthPos + 1);
    document.querySelectorAll(
      '#apdp-root .buybox-pill[data-option-position="' + position + '"]'
    ).forEach(function (pill) {
      var selected = pill.getAttribute('data-option-value') === lengthValue;
      pill.classList.toggle('is-selected', selected);
      pill.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    if (idEl) idEl.value = variant.id;

    window.apdpUpdateVariant(lengthSelect);

    var resolvedOptions = getSelectedOptionValues();
    var optionsCoherent = (variant.options || []).length === resolvedOptions.length &&
      resolvedOptions.every(function (value, index) {
        return String(value) === String((variant.options || [])[index]);
      });
    var idCoherent = !idEl || String(idEl.value) === String(variant.id);
    var priceEl = getPriceEl();
    var priceCoherent = !priceEl || !variant.price ||
      priceEl.getAttribute('data-original-price-pennies') === String(variant.price);
    if (optionsCoherent && idCoherent && priceCoherent) return true;

    Array.prototype.forEach.call(selects, function (select, index) {
      select.value = selectedOptions[index];
      var positionToRestore = String(index + 1);
      document.querySelectorAll(
        '#apdp-root .buybox-pill[data-option-position="' + positionToRestore + '"]'
      ).forEach(function (pill) {
        var selected = pill.getAttribute('data-option-value') === selectedOptions[index];
        pill.classList.toggle('is-selected', selected);
        pill.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    });
    if (idEl) idEl.value = previousVariantId;
    if (selects.length) window.apdpUpdateVariant(selects[0]);
    return false;
  }

  // ── Lock the customer-facing pills ───────────────────────────────────────
  // The native selects are already hidden and non-focusable. Keep them
  // enabled: disabling them makes third-party product-option scripts treat the
  // chosen variant as missing and can reset Safari/WebKit to the first colour.
  function disablePills() {
    getPills().forEach(function (pill) {
      pill.classList.add(DISABLED_CLS);
      pill.setAttribute('aria-disabled', 'true');
    });
  }

  // ── Re-enable the customer-facing pills ─────────────────────────────────
  function enablePills() {
    getPills().forEach(function (pill) {
      pill.classList.remove(DISABLED_CLS);
      pill.setAttribute('aria-disabled', 'false');
    });
  }

  // ── Message element: create once, reuse ───────────────────────────────────
  function ensureMessage() {
    var existing = document.getElementById(MSG_ID);
    if (existing) return existing;

    var msg = document.createElement('div');
    msg.id = MSG_ID;
    msg.className = 'buybox-cut-message';
    msg.setAttribute('role', 'alert');
    msg.setAttribute('aria-live', 'polite');
    msg.setAttribute('tabindex', '0');
    // Warning icon + text. SVG is presentational (aria-hidden).
    msg.innerHTML =
      '<svg aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 24 24" ' +
      'fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
      'stroke-linejoin="round" style="flex-shrink:0;display:inline-block;vertical-align:middle;">' +
      '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>' +
      '<line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' +
      '<span>Remove Made to Measure value</span>';

    // Clicking the message focuses the input and selects its content
    msg.addEventListener('click', function () {
      var inp = getInput();
      if (!inp) return;
      inp.focus();
      inp.select();
    });
    msg.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); msg.click(); }
    });

    // Place the message inline with the "Custom cut length" label so it sits
    // next to / in front of the label text rather than below the input.
    var label = document.querySelector('#apdp-root .buybox-field--cut .buybox-field-label');
    if (label) {
      label.appendChild(msg);
    } else {
      var cutField = document.querySelector('#apdp-root .buybox-field--cut');
      if (cutField) cutField.appendChild(msg);
    }
    return msg;
  }

  function showMessage() {
    var msg = ensureMessage();
    msg.classList.remove('buybox-cut-message--fade-out');
    msg.style.display = '';
    clearTimeout(_fadeTimer);
    _fadeTimer = setTimeout(function () {
      msg.classList.add('buybox-cut-message--fade-out');
    }, FADE_MS);
  }

  function hideMessage() {
    clearTimeout(_fadeTimer);
    var msg = document.getElementById(MSG_ID);
    if (msg) {
      msg.classList.add('buybox-cut-message--fade-out');
    }
  }

  // ── Announce state changes to screen readers ──────────────────────────────
  function ensureLiveRegion() {
    var existing = document.getElementById(LIVE_ID);
    if (existing) return existing;
    var region = document.createElement('div');
    region.id = LIVE_ID;
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.className = 'sr-only';
    document.body.appendChild(region);
    return region;
  }

  function announce(text) {
    var r = ensureLiveRegion();
    r.textContent = '';
    // Double-tick forces screen readers to re-read the same string if repeated
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { r.textContent = text; });
    });
  }

  // ── Price-per-metre recalculation ─────────────────────────────────────────
  // Reads data-price-per-metre-pennies from the price element's parent block
  // (.buybox-price-block), calculates Math.ceil(metres) × rate, and updates
  // #apdpPrice directly — bypassing apdpUpdateVariant so the two don't fight.
  //
  // The subtitle element (#apdp-cut-price-subtitle) is appended once to
  // .buybox-price-block and toggled on/off.

  function getPricePerMetrePennies() {
    // Attribute lives on the cut-field wrapper per Liquid template (line 578).
    // Fallback to .buybox-price-block for backwards compatibility.
    var src = document.querySelector('.buybox-field--cut[data-price-per-metre-pennies]')
           || document.querySelector('.buybox-price-block[data-price-per-metre-pennies]');
    if (!src) return 0;
    var raw = src.getAttribute('data-price-per-metre-pennies');
    if (!raw) return 0;
    var n = parseInt(raw, 10);
    return (isFinite(n) && n > 0) ? n : 0;
  }

  // ── Switchboard add-on getter ─────────────────────────────────────────────
  // Returns integer pennies from every selected option with data-price-pennies.
  // Supports both the legacy #apdp-cable-length select and dynamic Switchboard
  // add-on selects such as #apdp-addon-1.
  function getCableAddonPennies() {
    var selects = getAddonSelects();
    var legacy = getCableSelect();
    if (!selects.length && legacy) selects = [legacy];

    return selects.reduce(function(total, sel) {
      var opt = sel && sel.options ? sel.options[sel.selectedIndex] : null;
      if (!opt) return total;
      var raw = opt.getAttribute('data-price-pennies');
      var n = parseInt(raw, 10);
      return total + ((isFinite(n) && n >= 0) ? n : 0);
    }, 0);
  }

  function getOriginalPricePennies() {
    // First choice: currently selected variant from __apdpVariants__.
    // The visible price element may already include add-on pricing, so the
    // variant id is the safest source of the base product price.
    var idEl = getVariantId();
    if (idEl && window.__apdpVariants__) {
      var id = parseInt(idEl.value, 10);
      var variant = window.__apdpVariants__.find(function (v) { return v.id === id; });
      if (variant && variant.price) return variant.price;
    }
    // Second choice: data attribute on the price element itself.
    var priceEl = getPriceEl();
    if (priceEl) {
      var raw = priceEl.getAttribute('data-original-price-pennies');
      if (raw) {
        var n = parseInt(raw, 10);
        if (isFinite(n) && n > 0) return n;
      }
    }
    return 0;
  }

  function formatMoney(pennies) {
    var symbol = window.__atomCurrencySymbol__ || '£';
    return symbol + (pennies / 100).toFixed(2);
  }

  function syncAppOptionPrice() {
    if (typeof window.apdpSyncAppOptionPrice === 'function') {
      window.apdpSyncAppOptionPrice();
    }
  }

  function ensurePriceSubtitle() {
    var existing = document.getElementById(SUBTITLE_ID);
    if (existing) return existing;
    var sub = document.createElement('span');
    sub.id = SUBTITLE_ID;
    sub.className = 'buybox-price-cut-subtitle';
    sub.setAttribute('aria-live', 'polite');
    sub.style.display = 'none';
    var block = document.querySelector('.buybox-price-block');
    if (block) block.appendChild(sub);
    return sub;
  }

  function showPriceSubtitle(billedMetres, basePennies, requestedMetres) {
    var sub = ensurePriceSubtitle();
    sub.textContent = billedMetres + ' metre price applied (' + formatMoney(basePennies) +
      ') for your ' + formatMetresWithUnit(requestedMetres) + ' cut';
    sub.style.display = '';
    // Tick-defer so the display:block has painted before opacity transitions in
    requestAnimationFrame(function () {
      sub.classList.add('is-visible');
    });
  }

  function hidePriceSubtitle() {
    var sub = document.getElementById(SUBTITLE_ID);
    if (sub) {
      sub.classList.remove('is-visible');
      // Delay display:none until after the CSS transition finishes
      setTimeout(function () { if (sub) sub.style.display = 'none'; }, 210);
    }
  }

  // ── recalcPrice ───────────────────────────────────────────────────────────
  // Total = the exact Shopify variant selected for the billed whole-metre length
  // plus selected Switchboard add-ons. The variant is the checkout source of
  // truth, so the displayed price cannot drift from what Shopify will charge.
  function recalcPrice(metres) {
    var rate = getPricePerMetrePennies();
    var selectedVariant = getSelectedVariant();
    var lengthPos = getLengthOptionIndex();
    var billedMetres = selectedVariant && lengthPos >= 0
      ? toMetres((selectedVariant.options || [])[lengthPos])
      : null;
    if (!billedMetres) billedMetres = Math.ceil(metres);

    var basePennies = selectedVariant && selectedVariant.price
      ? selectedVariant.price
      : billedMetres * rate;
    if (!basePennies) {
      console.warn('[atom-custom-length] selected variant price and per-metre fallback are unavailable — price recalc skipped');
      return;
    }

    var cableAddonPennies = getCableAddonPennies();
    var totalPennies = basePennies + cableAddonPennies;

    var priceEl = getPriceEl();
    if (!priceEl) return;

    // Stamp the original price once so we can restore it later
    if (!priceEl.hasAttribute('data-original-price-pennies')) {
      // Try to read directly from the live __apdpVariants__ first
      var idEl = getVariantId();
      var originalPennies = 0;
      if (idEl && window.__apdpVariants__) {
        var id = parseInt(idEl.value, 10);
        var variant = window.__apdpVariants__.find(function (v) { return v.id === id; });
        if (variant && variant.price) originalPennies = variant.price;
      }
      if (!originalPennies) {
        // Parse whatever is currently displayed as a fallback
        var currentText = priceEl.textContent.replace(/[^0-9.]/g, '');
        originalPennies = Math.round(parseFloat(currentText) * 100) || 0;
      }
      priceEl.setAttribute('data-original-price-pennies', String(originalPennies));
    }

    priceEl.textContent = formatMoney(totalPennies);
    priceEl.setAttribute('data-mtm-price-active', 'true');
    priceEl.setAttribute('data-mtm-display-price-pennies', String(totalPennies));
    // Pulse the price element so the customer notices the recalculation
    priceEl.classList.remove('is-pulsing');
    // Force reflow so re-adding the class re-triggers the animation
    void priceEl.offsetWidth;
    priceEl.classList.add('is-pulsing');
    priceEl.addEventListener('animationend', function onEnd() {
      priceEl.classList.remove('is-pulsing');
      priceEl.removeEventListener('animationend', onEnd);
    });
    showPriceSubtitle(billedMetres, basePennies, metres);
    syncAppOptionPrice();
    var displayedPennies = parseInt(
      priceEl.getAttribute('data-current-display-price-pennies'),
      10
    );
    if (!isFinite(displayedPennies) || displayedPennies <= 0) displayedPennies = totalPennies;
    announce('Price updated: ' + formatMoney(displayedPennies) + ' using the ' + billedMetres + ' metre variant.');
  }

  // ── restoreOriginalPrice ──────────────────────────────────────────────────
  // When the cut-length field is cleared, display the base variant price plus
  // any currently selected cable add-on, then wipe the stamped attribute.
  function restoreOriginalPrice() {
    hidePriceSubtitle();
    var priceEl = getPriceEl();
    if (!priceEl) return;

    var originalPennies = getOriginalPricePennies();
    if (!originalPennies) return;

    var cableAddonPennies = getCableAddonPennies();
    priceEl.textContent = formatMoney(originalPennies + cableAddonPennies);
    priceEl.removeAttribute('data-mtm-price-active');
    priceEl.removeAttribute('data-mtm-display-price-pennies');
    // Brief pulse so the customer notices the price has restored
    priceEl.classList.remove('is-pulsing');
    void priceEl.offsetWidth;
    priceEl.classList.add('is-pulsing');
    priceEl.addEventListener('animationend', function onRestoreEnd() {
      priceEl.classList.remove('is-pulsing');
      priceEl.removeEventListener('animationend', onRestoreEnd);
    });
    syncAppOptionPrice();
  }

  // ── applyBaseVariantPriceWithCable ────────────────────────────────────────
  // Used when cable changes but cut-length is empty: show variant price + cable.
  function applyBaseVariantPriceWithCable() {
    var priceEl = getPriceEl();
    if (!priceEl) return;
    var basePennies = getOriginalPricePennies();
    if (!basePennies) return;
    var cableAddonPennies = getCableAddonPennies();
    priceEl.textContent = formatMoney(basePennies + cableAddonPennies);
    priceEl.removeAttribute('data-mtm-price-active');
    priceEl.removeAttribute('data-mtm-display-price-pennies');
    priceEl.classList.remove('is-pulsing');
    void priceEl.offsetWidth;
    priceEl.classList.add('is-pulsing');
    priceEl.addEventListener('animationend', function onAddonEnd() {
      priceEl.classList.remove('is-pulsing');
      priceEl.removeEventListener('animationend', onAddonEnd);
    });
    syncAppOptionPrice();
  }

  // ── Handle "cut length has a value" ──────────────────────────────────────
  function onCutLengthActive(metres) {
    if (!_cutLengthActive) {
      if (!snapshotPills()) {
        enablePills();
        setCutLengthValidity(UNAVAILABLE_LENGTH_MESSAGE);
        showCutLengthNotice(UNAVAILABLE_LENGTH_MESSAGE);
        clearMtmPriceState();
        announce(UNAVAILABLE_LENGTH_MESSAGE);
        return;
      }
      _cutLengthActive = true;
    }

    if (!restoreSnapshotSelectionForMtm()) {
      enablePills();
      setCutLengthValidity(UNAVAILABLE_LENGTH_MESSAGE);
      showCutLengthNotice(UNAVAILABLE_LENGTH_MESSAGE);
      clearMtmPriceState();
      announce(UNAVAILABLE_LENGTH_MESSAGE);
      return;
    }

    var maxMetres = getMaxVariantMetres();
    if (maxMetres && isOverMaxVariantLength(metres)) {
      onCutLengthInvalid(maxMetres);
      return;
    }

    clearCutLengthValidity();
    var result = findBestVariant(metres);
    if (!result || !applyVariant(result.variant)) {
      enablePills();
      setCutLengthValidity(UNAVAILABLE_LENGTH_MESSAGE);
      showCutLengthNotice(UNAVAILABLE_LENGTH_MESSAGE);
      clearMtmPriceState();
      announce(UNAVAILABLE_LENGTH_MESSAGE);
      return;
    }

    disablePills();
    hideCappedHint();
    // Recalc after applyVariant so the original price stamp is captured correctly
    recalcPrice(metres);
    announce('Variant selection locked to the matching ' + result.billedMetres + ' metre option.');
  }

  function setCutLengthValidity(message) {
    var inp = getInput();
    if (!inp) return;
    inp.setCustomValidity(message || '');
    var describedBy = (inp.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean)
      .filter(function (id) { return id !== CAPPED_ID; });
    if (message) {
      ensureCutLengthNotice();
      describedBy.push(CAPPED_ID);
      inp.setAttribute('aria-invalid', 'true');
      inp.setAttribute('aria-errormessage', CAPPED_ID);
    } else {
      inp.removeAttribute('aria-invalid');
      inp.removeAttribute('aria-errormessage');
    }
    if (describedBy.length) inp.setAttribute('aria-describedby', describedBy.join(' '));
    else inp.removeAttribute('aria-describedby');
  }

  function clearCutLengthValidity() {
    setCutLengthValidity('');
    hideCappedHint();
  }

  function clearMtmPriceState() {
    hidePriceSubtitle();
    var priceEl = getPriceEl();
    if (!priceEl) return;
    priceEl.removeAttribute('data-mtm-price-active');
    priceEl.removeAttribute('data-mtm-display-price-pennies');
  }

  function onCutLengthInvalid(maxMetres) {
    var message = buildMtmLimitMessage(maxMetres);
    setCutLengthValidity(message);
    enablePills();
    clearMtmPriceState();
    restoreOriginalPrice();
    showCappedHint(maxMetres);
    announce(message);
  }

  function onCutLengthMalformed() {
    setCutLengthValidity(INVALID_LENGTH_MESSAGE);
    enablePills();
    clearMtmPriceState();
    restoreOriginalPrice();
    showCutLengthNotice(INVALID_LENGTH_MESSAGE);
    announce(INVALID_LENGTH_MESSAGE);
  }

  // ── Handle "cut length is empty" ──────────────────────────────────────────
  function onCutLengthCleared() {
    clearCutLengthValidity();
    enablePills();
    if (_cutLengthActive) restorePills(true);
    _cutLengthActive = false;
    _priorVariantId = null;
    _priorPillValues = {};
    hideMessage();
    restoreOriginalPrice();
    announce('Variant selection restored.');
  }

  // ── Max-length guidance when MTM exceeds existing Shopify variants ───────
  var CAPPED_ID = 'apdp-cut-capped';

  function ensureCutLengthNotice() {
    var el = document.getElementById(CAPPED_ID);
    if (el) return el;
    el = document.createElement('p');
    el.id = CAPPED_ID;
    el.className = 'buybox-cut-capped';
    var text = document.createElement('span');
    text.setAttribute('data-apdp-cut-notice-text', '');
    var contact = document.createElement('button');
    contact.type = 'button';
    contact.className = 'buybox-cut-contact';
    contact.setAttribute('data-apdp-mtm-contact-open', 'true');
    contact.setAttribute('aria-controls', 'apdp-mtm-contact-modal');
    contact.setAttribute('aria-haspopup', 'dialog');
    contact.setAttribute('tabindex', '0');
    contact.textContent = 'contact us';
    contact.hidden = true;
    var suffix = document.createElement('span');
    suffix.setAttribute('data-apdp-cut-notice-suffix', '');
    suffix.textContent = '.';
    suffix.hidden = true;
    el.appendChild(text);
    el.appendChild(contact);
    el.appendChild(suffix);
    var cutField = document.querySelector('#apdp-root .buybox-field--cut');
    if (cutField) cutField.appendChild(el);
    return el;
  }

  function showCutLengthNotice(message) {
    var el = ensureCutLengthNotice();
    var text = el.querySelector('[data-apdp-cut-notice-text]');
    var contact = el.querySelector('[data-apdp-mtm-contact-open]');
    var suffix = el.querySelector('[data-apdp-cut-notice-suffix]');
    if (text) text.textContent = message;
    if (contact) contact.hidden = true;
    if (suffix) suffix.hidden = true;
    el.style.display = '';
  }

  function showCappedHint(maxMetres) {
    var el = ensureCutLengthNotice();

    maxMetres = maxMetres || getMaxVariantMetres();
    var text = el.querySelector('[data-apdp-cut-notice-text]');
    var contact = el.querySelector('[data-apdp-mtm-contact-open]');
    var suffix = el.querySelector('[data-apdp-cut-notice-suffix]');
    var limit = maxMetres ? ' ' + formatMetresWithUnit(maxMetres) : '';
    if (text) text.textContent = 'Only max length allowed' + limit + '. For longer lengths ';
    if (contact) contact.hidden = false;
    if (suffix) suffix.hidden = false;
    el.style.display = '';
  }

  function hideCappedHint() {
    var el = document.getElementById(CAPPED_ID);
    if (el) el.style.display = 'none';
  }

  function showMtmLimitMessage() {
    var maxMetres = getMaxVariantMetres();
    var message = buildMtmLimitMessage(maxMetres);
    showCappedHint(maxMetres);
    announce(message);
  }

  function getInputValueAfterBeforeInput(inp, event) {
    if (!inp || !event) return null;
    if (event.inputType && event.inputType.indexOf('delete') === 0) return null;
    if (event.inputType === 'insertCompositionText') return null;

    var inserted = typeof event.data === 'string' ? event.data : '';
    if (!inserted && event.inputType !== 'insertFromPaste') return null;

    var start = typeof inp.selectionStart === 'number' ? inp.selectionStart : inp.value.length;
    var end = typeof inp.selectionEnd === 'number' ? inp.selectionEnd : start;
    return inp.value.slice(0, start) + inserted + inp.value.slice(end);
  }

  function handleBeforeInput(event) {
    var inp = event.target;
    var nextValue = getInputValueAfterBeforeInput(inp, event);
    if (nextValue === null) return;

    var metres = parseLengthValue(nextValue);
    if (metres !== null && isOverMaxVariantLength(metres)) {
      showMtmLimitMessage();
    }
  }

  // ── Core input handler ────────────────────────────────────────────────────
  function handleInput() {
    var inp = getInput();
    if (!inp) return;
    var raw = String(inp.value || '').trim();
    if (!raw) {
      onCutLengthCleared();
      return;
    }

    var metres = parseLength(inp);
    if (metres !== null) {
      onCutLengthActive(metres);
    } else {
      onCutLengthMalformed();
    }
  }

  // Final checkout-path invariant: immediately before the theme builds the
  // atomic cart payload, recompute MTM from its preserved customer-visible
  // selection and require the hidden variant id to match the resolved options.
  window.apdpEnsureMtmSelection = function () {
    var inp = getInput();
    var raw = inp ? String(inp.value || '').trim() : '';
    if (!raw) return reconcileVisibleVariantSelection();

    handleInput();
    if (!inp || inp.getAttribute('aria-invalid') === 'true' || !inp.checkValidity()) return false;

    var values = getSelectedOptionValues();
    var exactVariant = exactVariantForOptions(values);
    var idEl = getVariantId();
    return Boolean(exactVariant && idEl && String(idEl.value) === String(exactVariant.id));
  };

  // ── Cable dropdown change handler ─────────────────────────────────────────
  // Recalculates the displayed price whenever the cable option changes.
  // If a cut length is active, delegates to recalcPrice; otherwise uses the
  // base variant price helper so cut-less pages stay correct.
  function handleCableChange() {
    var inp = getInput();
    var metres = inp ? parseLength(inp) : null;
    if (metres !== null) {
      if (isOverMaxVariantLength(metres)) {
        onCutLengthInvalid(getMaxVariantMetres());
        return;
      }
      recalcPrice(metres);
    } else {
      applyBaseVariantPriceWithCable();
    }
  }

  window.apdpRefreshSwitchboardPrice = handleCableChange;

  // ── Intercept pill clicks when disabled ──────────────────────────────────
  function handlePillClick(e) {
    var btn = e.target.closest('.buybox-pill');
    if (!btn) return;
    if (btn.classList.contains(DISABLED_CLS)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      showMessage();
    }
  }

  function getActiveMtmValidationMessage() {
    var inp = getInput();
    if (!inp || !String(inp.value || '').trim()) return '';

    var metres = parseLength(inp);
    if (metres === null) return INVALID_LENGTH_MESSAGE;

    var maxMetres = getMaxVariantMetres();
    if (maxMetres && isOverMaxVariantLength(metres)) {
      return buildMtmLimitMessage(maxMetres);
    }

    if (inp.getAttribute('aria-invalid') === 'true' && inp.validationMessage) {
      return inp.validationMessage;
    }
    return '';
  }

  function blockInvalidCartAction(e) {
    var message = getActiveMtmValidationMessage();
    if (!message) {
      if (window.apdpEnsureMtmSelection()) return false;
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      return true;
    }

    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    setCutLengthValidity(message);

    var inp = getInput();
    var metres = inp ? parseLength(inp) : null;
    var maxMetres = getMaxVariantMetres();
    if (metres !== null && maxMetres && isOverMaxVariantLength(metres)) {
      showCappedHint(maxMetres);
    } else {
      showCutLengthNotice(message);
    }
    announce(message);
    if (inp && typeof inp.reportValidity === 'function') inp.reportValidity();
    return true;
  }

  function guardCartButtonActivation(e) {
    var target = e.target && e.target.closest ? e.target.closest('.btn-cart') : null;
    if (!target) return;
    blockInvalidCartAction(e);
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function init() {
    var inp = getInput();
    var form = getForm();

    ensureLiveRegion();

    if (inp) {
      var maxMetres = getMaxVariantMetres();
      if (maxMetres) {
        inp.setAttribute('data-max-mtm-metres', formatMetres(maxMetres));
      }
      inp.addEventListener('beforeinput', handleBeforeInput);
      inp.addEventListener('input', handleInput);
      inp.addEventListener('change', handleInput);
    }

    getSelects().forEach(function (select) {
      select.addEventListener('change', function () {
        if (inp && String(inp.value || '').trim()) handleInput();
      });
    });

    window.apdpOpenMtmContactModal = function (trigger) {
      return openMtmContactModal(trigger || document.activeElement);
    };

    document.addEventListener('pointerdown', handleMtmContactActivation, true);
    document.addEventListener('mousedown', handleMtmContactActivation, true);
    document.addEventListener('touchstart', handleMtmContactActivation, { capture: true, passive: false });
    document.addEventListener('click', handleMtmContactActivation, true);
    document.addEventListener('keydown', handleMtmContactKeydown, true);
    syncContactProductFields();

    var shouldShowContactSuccess = hasMtmContactSuccessSignal();
    if (shouldShowContactSuccess) {
      setMtmContactStatus('success');
      setMtmContactSubmitted(getMtmContactForm());
      openMtmContactModal(null, { preserveStatus: true });
      clearMtmContactPostedUrl();
    } else {
      resetMtmContactStatus();
      clearMtmContactPostedUrl();
    }

    var contactForm = document.getElementById('apdp-mtm-contact-form');
    if (contactForm) {
      contactForm.addEventListener('focusin', function (event) {
        warmMtmContactProtection();
        if (contactForm.dataset.apdpMtmSubmitted === 'true') return;
        if (event.target && event.target.matches && event.target.matches('input, textarea')) {
          resetMtmContactStatus();
        }
      });
      contactForm.addEventListener('submit', handleMtmContactSubmit, true);
    }

    setTimeout(warmMtmContactProtection, 800);
    window.addEventListener('pageshow', function (event) {
      setMtmContactSubmitting(getMtmContactForm(), false);
      if (event.persisted) {
        handleInput();
        return;
      }
      if (!isBackForwardNavigation()) return;
      var reconcile = function () {
        var inp = getInput();
        if (!inp || String(inp.value || '').trim()) return;
        reconcileVisibleVariantSelection();
      };
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(reconcile);
      else reconcile();
    });

    if (form) {
      form.addEventListener('submit', blockInvalidCartAction, true);
      form.addEventListener('mousedown', guardCartButtonActivation, true);
      form.addEventListener('touchstart', guardCartButtonActivation, { capture: true, passive: false });
      form.addEventListener('click', guardCartButtonActivation, true);
    }

    // Switchboard add-on dropdowns — bind every priced select on this page.
    var addonSelects = getAddonSelects();
    if (!addonSelects.length && getCableSelect()) addonSelects = [getCableSelect()];
    addonSelects.forEach(function(select) {
      select.addEventListener('change', handleCableChange);
    });

    if (addonSelects.length) {
      // Requirement 5 (cable): if a non-zero add-on option is already selected
      // on load (browser autofill / back-button), apply its add-on immediately.
      if (getCableAddonPennies() > 0) {
        handleCableChange();
      }
    }

    // Intercept clicks on the pill container — capture phase so we run before
    // the existing onclick="apdpSelectVariantPill(this)" handler fires.
    var root = document.getElementById('apdp-root');
    if (root) {
      root.addEventListener('click', handlePillClick, true);
    }

    // Requirement 5: apply state immediately if the input is already filled
    // (browser autofill or back-button restore).
    if (inp && parseLength(inp) !== null) {
      handleInput();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
